import type { MutableRefObject } from "react";
import type { Action } from "@core/features/actions";
import {
  isOrgAdminAction,
  useCompleteOrgAction,
  ACTION_TYPE_SLUG,
} from "@core/features/actions";
import { InvitationFormContainer } from "@platform/features/invitation";
import { Loader } from "@themes/pollinator/ui/Loader";
import { Type } from "@themes/pollinator/ui/Type";
import type { StepValidationState } from "../../onboarding.types";
import { useOnboardingInvitation } from "../../hooks/useOnboardingInvitation";
import { ErrorState } from "../StepContent/ErrorState";

export type Props = {
  /** The action for this step */
  action: Action;
  /** Callback when the step is completed */
  onComplete: () => void;
  /** Callback when the step is skipped (optional) */
  onSkip?: () => void;
  /** Whether to show action buttons in the step content (default: true) */
  showActionButtons?: boolean;
  /** Callback when step validation state changes (for parent footer) */
  onStepStateChange?: (state: StepValidationState) => void;
  /** Ref to expose the submit handler to parent */
  submitRef?: MutableRefObject<(() => Promise<void> | void) | null>;
};

/**
 * Container that resolves the invitation for the onboarding invitation step.
 *
 * For org admin: Fetches/creates orgAdmin invitation, marks action complete on success.
 * For participant: Uses invitation from action.target_object_data directly.
 */
export function OnboardingInvitationStepContainer({
  action,
  onComplete,
  showActionButtons = true,
  onStepStateChange,
  submitRef,
}: Props) {
  const { invitation, isLoading, error } = useOnboardingInvitation({ action });
  const isOrgAdmin = isOrgAdminAction(action);

  // For org admin, get the organizationId to complete the action
  const orgData = action.target_object_data as { id?: number } | null;
  const organizationId = isOrgAdmin ? orgData?.id ?? 0 : 0;

  // Hook to complete the org admin action
  const { complete: completeOrgAction } = useCompleteOrgAction({
    actionTypeSlug: ACTION_TYPE_SLUG.SEND_INVITATION,
    organizationId,
    programId: action.program,
  });

  // Handle successful form submission
  const handleSuccess = () => {
    if (isOrgAdmin) {
      completeOrgAction();
    }
    onComplete();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader />
      </div>
    );
  }

  if (error) {
    return <ErrorState message="Failed to load invitation. Please try again." />;
  }

  if (!invitation?.id) {
    return <ErrorState message="No invitation available." />;
  }

  const inviteHeading = isOrgAdmin
    ? "Invite Team Members"
    : "Invite Friends";

  const inviteDescription = isOrgAdmin
    ? "Add colleagues to help manage your organization. They'll receive an email invitation to join."
    : "Know someone who's aligned with Pollinator's ethos, but isn't yet eligible? We trust you to bring great people to our growing community of artists, technologists, and changemakers. Share their info below, and they'll receive an email invitation to join.";

  return (
    <div className="w-full flex flex-col flex-1">
      <div className="flex flex-col gap-2">
        <Type variant="h2">{inviteHeading}</Type>
      </div>
      <div className="flex-1 flex flex-col justify-center">
        <div className="flex flex-col gap-8">
          <Type variant="body1" className="text-brand-700 font-medium">
            {inviteDescription}
          </Type>
          <InvitationFormContainer
            invitationId={invitation.id}
            maxUses={invitation.max_uses}
            onSuccess={handleSuccess}
            showHeading={false}
            showUsageHint={false}
            showActionButtons={showActionButtons}
            onStepStateChange={onStepStateChange}
            submitRef={submitRef}
            emailPlaceholder={isOrgAdmin ? "colleague@company.com" : undefined}
          />
        </div>
      </div>
    </div>
  );
}

export default OnboardingInvitationStepContainer;
